/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { jsx } from 'react/jsx-runtime';
import { createContext, useContext } from 'react';
import { derived } from '@vscode/observables';

function createServiceKey(nameOrCtor) {
    const name = typeof nameOrCtor === 'string' ? nameOrCtor : nameOrCtor.name;
    return { _brand: undefined, id: Symbol(name), name };
}
// =============================================================================
// DI Container
// =============================================================================
class DIContainer {
    constructor(parent = null) {
        this._services = new Map();
        this._parent = parent;
    }
    register(key, service) {
        this._services.set(key.id, service);
        return this;
    }
    get(key) {
        const service = this._services.get(key.id);
        if (service !== undefined)
            return service;
        if (this._parent)
            return this._parent.get(key);
        throw new Error(`Service "${key.name}" not registered`);
    }
    has(key) {
        return this._services.has(key.id) || (this._parent?.has(key) ?? false);
    }
    createChild() {
        return new DIContainer(this);
    }
}
// =============================================================================
// React Context
// =============================================================================
const DIContext = createContext(null);
function DIProvider({ container, children }) {
    return jsx(DIContext.Provider, { value: container, children: children });
}
function useDIContainer() {
    const container = useContext(DIContext);
    if (!container)
        throw new Error("DIProvider not found");
    return container;
}
// =============================================================================
// inject() - Property Transformer for DI
// =============================================================================
class InjectTransformerFactory {
    constructor(serviceKey) {
        this.serviceKey = serviceKey;
        this._requiredContext = DIContext;
    }
    create(readable, contextValue) {
        const container = contextValue;
        let cachedService;
        return derived(reader => {
            // Check if an explicit value was provided as a prop
            const explicitValue = readable(reader);
            if (explicitValue !== undefined) {
                return explicitValue;
            }
            // Otherwise, use DI container (cached)
            if (cachedService === undefined) {
                if (!container)
                    throw new Error(`inject(${this.serviceKey.name}): DIProvider not found`);
                cachedService = container.get(this.serviceKey);
            }
            return cachedService;
        });
    }
}
/** Inject a service from DIContainer into a ViewModel property */
function inject(key) {
    return new InjectTransformerFactory(key);
}

export { DIContainer, DIContext, DIProvider, createServiceKey, inject, useDIContainer };
//# sourceMappingURL=di.js.map
